import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { CompetenceChips } from './CompetenceChips';
import { useInviteCollaborator } from '../../hooks/queries/useCollaborators';
import { cn } from '../../lib/cn';
import type { CollaboratorRole } from '../../services/collaborators/collaboratorService';

interface InviteFormValues {
  full_name: string;
  email: string;
  role: CollaboratorRole;
  phone: string;
  bar_number: string;
  category_ids: string[];
}

interface InviteCollaboratorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InviteCollaboratorModal({ open, onOpenChange }: InviteCollaboratorModalProps) {
  const { mutateAsync: invite, isPending } = useInviteCollaborator();

  const {
    register,
    handleSubmit,
    watch,
    control,
    reset,
    setError,
    formState: { errors },
  } = useForm<InviteFormValues>({
    defaultValues: {
      full_name: '',
      email: '',
      role: 'lawyer',
      phone: '',
      bar_number: '',
      category_ids: [],
    },
  });

  const role = watch('role');

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  async function onSubmit(values: InviteFormValues) {
    try {
      await invite({
        email: values.email,
        full_name: values.full_name,
        role: values.role,
        phone: values.phone || null,
        bar_number: values.bar_number || null,
        category_ids: values.category_ids,
      });

      toast.success(`Invitation envoyée à ${values.email}`);
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur inconnue';

      if (message.toLowerCase().includes('already exists') || message.toLowerCase().includes('déjà')) {
        setError('email', { message: 'Cette adresse email est déjà utilisée.' });
      } else {
        toast.error(`Erreur lors de l'invitation : ${message}`);
      }
    }
  }

  const showBarNumber = role === 'admin' || role === 'lawyer';

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50',
            'w-full max-w-lg max-h-[90vh] overflow-y-auto',
            'bg-cime-surface rounded-cime border border-neutral-200 shadow-xl',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-neutral-200">
            <Dialog.Title className="font-heading text-lg font-semibold text-neutral-900">
              Inviter un collaborateur
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                className="p-1.5 rounded-md text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors"
                aria-label="Fermer"
              >
                <X className="w-4 h-4" />
              </button>
            </Dialog.Close>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} noValidate className="p-6 space-y-5">
            {/* Full name */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="inv-full_name" className="text-sm font-medium text-neutral-700">
                Nom complet <span className="text-red-500">*</span>
              </label>
              <Input
                id="inv-full_name"
                placeholder="Maître Jean Dupont"
                aria-invalid={!!errors.full_name}
                {...register('full_name', { required: 'Le nom est requis' })}
              />
              {errors.full_name && (
                <p className="text-xs text-red-600" role="alert">{errors.full_name.message}</p>
              )}
            </div>

            {/* Email */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="inv-email" className="text-sm font-medium text-neutral-700">
                Adresse email <span className="text-red-500">*</span>
              </label>
              <Input
                id="inv-email"
                type="email"
                placeholder="jean.dupont@cabinet.fr"
                aria-invalid={!!errors.email}
                {...register('email', {
                  required: "L'email est requis",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Email invalide',
                  },
                })}
              />
              {errors.email && (
                <p className="text-xs text-red-600" role="alert">{errors.email.message}</p>
              )}
            </div>

            {/* Role */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="inv-role" className="text-sm font-medium text-neutral-700">
                Rôle <span className="text-red-500">*</span>
              </label>
              <select
                id="inv-role"
                className="w-full h-10 px-3 text-sm rounded-cime border border-neutral-300 bg-white text-neutral-900 focus:outline-none focus:ring-2 focus:ring-cime-primary focus:border-transparent transition-colors"
                {...register('role')}
              >
                <option value="lawyer">Avocat</option>
                <option value="secretary">Assistant(e)</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            {/* Phone */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="inv-phone" className="text-sm font-medium text-neutral-700">
                Téléphone <span className="text-neutral-400 text-xs font-normal">(optionnel)</span>
              </label>
              <Input
                id="inv-phone"
                type="tel"
                placeholder="+33 6 12 34 56 78"
                {...register('phone')}
              />
            </div>

            {/* Bar number (only for admin/lawyer) */}
            {showBarNumber && (
              <div className="flex flex-col gap-1.5">
                <label htmlFor="inv-bar_number" className="text-sm font-medium text-neutral-700">
                  Numéro de barreau <span className="text-neutral-400 text-xs font-normal">(optionnel)</span>
                </label>
                <Input
                  id="inv-bar_number"
                  placeholder="75XXXX"
                  {...register('bar_number')}
                />
              </div>
            )}

            {/* Competences */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-neutral-700">
                Domaines de compétence
              </label>
              <Controller
                name="category_ids"
                control={control}
                render={({ field }) => (
                  <CompetenceChips value={field.value} onChange={field.onChange} />
                )}
              />
            </div>

            {/* Footer */}
            <div className="border-t border-neutral-200 pt-4 space-y-3">
              <p className="text-xs text-neutral-500">
                Le collaborateur recevra un email pour définir son mot de passe.
              </p>
              <div className="flex gap-3 justify-end">
                <Dialog.Close asChild>
                  <Button type="button" variant="ghost" disabled={isPending}>
                    Annuler
                  </Button>
                </Dialog.Close>
                <Button type="submit" variant="primary" isLoading={isPending}>
                  Envoyer l'invitation
                </Button>
              </div>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
